var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { getSearchProvider } from './providers';
import { readPreloadCache, clearPreloadCache, preloadLog } from './preload';
import { useAnalytics } from '../analytics/useAnalytics';
import { installMrRecommendationsRefresh } from '../utils/mrRecommendationsGlobal';
/**
 * Default sort when the shopper hasn't chosen one (no `?sort` in the URL).
 * "manual" makes Boost follow the Shopify Admin collection order, so the
 * default view matches the storefront (AQ-1045).
 */
const DEFAULT_SORT = 'manual';
const NON_FILTER_QUERY_PARAMS = new Set(['q', 'sort', 'pb', '_fd', 'preview_theme_id', 'preloadDebug']);
const PRELOAD_WAIT_TIMEOUT_MS = 2000;
export function useSearchProvider(providerType, mockPlatform, pageSize) {
    // Core search state
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState([]);
    const [meta, setMeta] = useState(null);
    const [sortOptions, setSortOptions] = useState([]);
    const [provider] = useState(() => getSearchProvider(providerType, mockPlatform));
    const analytics = useAnalytics();
    // Preload handoff from early-init-boost.js (see src/search/preload.ts).
    // `null` when unset — every branch below then behaves as before preload existed.
    const [preloadCache, setPreloadCache] = useState(() => readPreloadCache());
    const [hasCheckedPreload, setHasCheckedPreload] = useState(false);
    const [hasUsedPreloadedData, setHasUsedPreloadedData] = useState(false);
    const hasPreloadedData = !!((preloadCache === null || preloadCache === void 0 ? void 0 : preloadCache.ready) && (preloadCache === null || preloadCache === void 0 ? void 0 : preloadCache.data) && !(preloadCache === null || preloadCache === void 0 ? void 0 : preloadCache.error));
    // Search parameters state
    const [searchState, setSearchState] = useState({
        query: '',
        currentSort: DEFAULT_SORT,
        appliedFilters: {}
    });
    // URL parameter management
    const updateUrlParams = useCallback(({ query, currentSort, appliedFilters }) => {
        const urlParams = new URLSearchParams();
        // Only surface a non-default sort in the URL. The default (manual) is
        // implicit, so a fresh collection/search load stays clean (no ?sort=manual);
        // an explicit choice like price_asc is still written and shareable.
        if (currentSort && currentSort !== DEFAULT_SORT) {
            urlParams.set('sort', currentSort);
        }
        if (query) {
            urlParams.set('q', query);
        }
        Object.entries(appliedFilters).forEach(([key, values]) => {
            values.forEach(value => {
                urlParams.append(key, value);
            });
        });
        const queryString = urlParams.toString();
        window.history.replaceState({}, '', queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname);
    }, []);
    // Let the request started from <head> finish instead of competing with it.
    // If it is still pending after this deadline, abort it before falling back
    // to the normal provider request so only one Boost request is in flight.
    useEffect(() => {
        if (preloadCache && !preloadCache.ready && !preloadCache.error) {
            preloadLog('early-init in progress, waiting for completion…');
            const onSettled = () => {
                const cache = readPreloadCache();
                preloadLog('early-init settled, ready:', cache === null || cache === void 0 ? void 0 : cache.ready);
                setPreloadCache(cache);
                setHasCheckedPreload(true);
            };
            const timeout = window.setTimeout(() => {
                var _b;
                const cache = readPreloadCache();
                if (cache && !cache.ready && !cache.error) {
                    preloadLog('early-init timed out; aborting before live fallback');
                    (_b = cache.abort) === null || _b === void 0 ? void 0 : _b.call(cache);
                    setPreloadCache(null);
                    setHasCheckedPreload(true);
                }
            }, PRELOAD_WAIT_TIMEOUT_MS);
            window.addEventListener('mr:search-preload-settled', onSettled, { once: true });
            return () => {
                window.clearTimeout(timeout);
                window.removeEventListener('mr:search-preload-settled', onSettled);
            };
        }
        setHasCheckedPreload(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Initialize: use the preload if we have one, otherwise run the initial search.
    useEffect(() => {
        if (!hasCheckedPreload || hasUsedPreloadedData) {
            return;
        }
        if (hasPreloadedData && provider.setPreloadedData) {
            setHasUsedPreloadedData(true);
            const { query, sort, filters } = preloadCache.params;
            const newSearchState = {
                query: query || '',
                currentSort: sort || DEFAULT_SORT,
                appliedFilters: filters || {}
            };
            setSearchState(newSearchState);
            updateUrlParams(newSearchState);
            if (newSearchState.query)
                analytics.trackSearch({ searchTerm: newSearchState.query });
            preloadLog('using preloaded data — skipping initial API call');
            setLoading(true);
            (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    yield provider.setPreloadedData(preloadCache.data.rawResponse, {
                        sort: newSearchState.currentSort || undefined,
                        filters: newSearchState.appliedFilters,
                        pageSize
                    });
                    setProducts(provider.getProducts());
                    setFilters(provider.getFilters());
                    setMeta(provider.getMeta());
                    setSortOptions(provider.getSortOptions());
                }
                catch (error) {
                    // Never leave the grid empty — fall back to a live search.
                    preloadLog('preload apply failed — falling back to a live search:', error);
                    yield runSearch(newSearchState.query, {
                        sort: newSearchState.currentSort || undefined,
                        filters: newSearchState.appliedFilters,
                        pageSize
                    });
                }
                finally {
                    setLoading(false);
                }
            }))();
            clearPreloadCache();
            return;
        }
        // No usable preload — unchanged pre-preload behavior.
        setHasUsedPreloadedData(true);
        const urlParams = new URLSearchParams(window.location.search);
        const queryParam = urlParams.get('q');
        const sortParam = urlParams.get('sort');
        const initialFilters = {};
        urlParams.forEach((value, key) => {
            if (!NON_FILTER_QUERY_PARAMS.has(key)) {
                if (!initialFilters[key]) {
                    initialFilters[key] = [];
                }
                initialFilters[key].push(value);
            }
        });
        const newSearchState = {
            query: queryParam || '',
            currentSort: sortParam || DEFAULT_SORT,
            appliedFilters: initialFilters
        };
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        // Landing on a results page with a query (e.g. from a header-search submit
        // that navigated here) counts as a submitted search — see Boost's `a:search`.
        if (newSearchState.query)
            analytics.trackSearch({ searchTerm: newSearchState.query });
        runSearch(newSearchState.query, {
            sort: newSearchState.currentSort || undefined,
            filters: newSearchState.appliedFilters,
            pageSize
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasCheckedPreload, hasPreloadedData, hasUsedPreloadedData]);
    // Core search function
    const runSearch = (query_1, ...args_1) => __awaiter(this, [query_1, ...args_1], void 0, function* (query, options = {}) {
        setLoading(true);
        try {
            yield provider.search(query, options);
            setProducts(provider.getProducts());
            setFilters(provider.getFilters());
            setMeta(provider.getMeta());
            setSortOptions(provider.getSortOptions());
        }
        catch (error) {
            console.error('Search failed:', error);
        }
        finally {
            setLoading(false);
        }
    });
    // Event handlers
    const handleQuerySubmit = (newQuery) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { query: newQuery });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        if (newQuery)
            analytics.trackSearch({ searchTerm: newQuery });
        runSearch(newQuery, {
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters
        });
    };
    const handleFilterChange = (filterId, value) => {
        setSearchState(prev => {
            var _b;
            const newFilters = Object.assign({}, prev.appliedFilters);
            // pageType mirrors the TrackingProvider in UnifiedDemoPage (query → search,
            // else collection) so the filter event's `pg` matches the product-click's,
            // rather than the provider re-deriving it from the URL path.
            const pageType = prev.query ? 'search_page' : 'collection_page';
            if (filterId.includes('price')) {
                newFilters[filterId] = [value];
                analytics.trackSelectFilter({ filterId, value, searchTerm: prev.query || undefined, pageType });
            }
            else {
                if ((_b = newFilters[filterId]) === null || _b === void 0 ? void 0 : _b.includes(value)) {
                    newFilters[filterId] = newFilters[filterId].filter(v => v !== value);
                    if (newFilters[filterId].length === 0) {
                        delete newFilters[filterId];
                    }
                }
                else {
                    newFilters[filterId] = [...(newFilters[filterId] || []), value];
                    // Fire only on apply, not on de-select — matches Boost's `a:filter`
                    // "Clicked Filter".
                    analytics.trackSelectFilter({ filterId, value, searchTerm: prev.query || undefined, pageType });
                }
            }
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: newFilters });
            updateUrlParams(newSearchState);
            runSearch(prev.query, {
                sort: prev.currentSort || undefined,
                filters: newFilters
            });
            return newSearchState;
        });
    };
    const handleClearAllFilters = () => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { appliedFilters: {} });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.query, {
            sort: searchState.currentSort || undefined,
            filters: {}
        });
    };
    const handlePageChange = (page) => {
        runSearch(searchState.query, {
            page,
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters
        });
    };
    const handleSortChange = (sort) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { currentSort: sort });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.query, {
            sort: sort || undefined,
            filters: searchState.appliedFilters
        });
    };
    return {
        loading,
        products,
        filters,
        meta,
        sortOptions,
        query: searchState.query,
        currentSort: searchState.currentSort,
        appliedFilters: searchState.appliedFilters,
        handleQuerySubmit,
        handleFilterChange,
        handleClearAllFilters,
        handlePageChange,
        handleSortChange,
        runSearch
    };
}
// Register `window.mrRecommendations.refresh` at module load so the global is
// available anywhere `useRecsProvider` is imported. The install lives in its
// own helper module rather than being inlined here — see the comment in
// `utils/mrRecommendationsGlobal.ts` for the rationale (keeping the side
// effect explicit and the hook module focused on its actual job).
installMrRecommendationsRefresh();
export function useRecsProvider(providerType, mockPlatform, recsConfig) {
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [provider] = useState(() => getSearchProvider(providerType, mockPlatform));
    // Holds the AbortController for the most recent `loadRecs` call so the next
    // call can cancel it. See the comment at the top of `loadRecs` for why this
    // is structured per-call rather than per-mount.
    const inflightRef = useRef(null);
    // Hash the whole config so any option change (including provider-specific
    // extras we don't statically know about in the shared hook) triggers a re-memo.
    const recsConfigKey = JSON.stringify(recsConfig !== null && recsConfig !== void 0 ? recsConfig : null);
    const loadRecs = useCallback((override) => __awaiter(this, void 0, void 0, function* () {
        var _b, _c, _d;
        if (!(recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.recsType))
            return;
        // Abort any in-flight fetch before starting a new one. Two failure modes
        // both surface as "stale recommendations" without this guard:
        //   1. Out-of-order responses — a slow earlier request can resolve AFTER
        //      a faster later one and overwrite its results (`setProducts` has
        //      no last-write-wins protection).
        //   2. Burst calls — e.g. a series of `cart:updated` events as the
        //      shopper rapidly mutates qty would each fire `loadRecs` and flood
        //      Boost. Aborting collapses bursts into a single live request.
        // The aborted fetch never reaches the cache write inside the provider,
        // so cancellation can't poison the per-(seed,recsType,widgetId) cache.
        (_b = inflightRef.current) === null || _b === void 0 ? void 0 : _b.abort();
        const controller = new AbortController();
        inflightRef.current = controller;
        setLoading(true);
        try {
            const { seedProductId, recsType, awaitRefresh: _a } = recsConfig, options = __rest(recsConfig, ["seedProductId", "recsType", "awaitRefresh"]);
            const effectiveSeed = (_d = (_c = override === null || override === void 0 ? void 0 : override.seedProductId) !== null && _c !== void 0 ? _c : seedProductId) !== null && _d !== void 0 ? _d : '';
            console.log('effectiveSeed', effectiveSeed, override);
            // The rest is structurally equivalent to TOptions (RecsConfig = TOptions &
            // known hook fields), but TS can't prove that statically — cast through.
            yield provider.fetchRecs(effectiveSeed, recsType, options, controller.signal);
            if (controller.signal.aborted)
                return;
            setProducts(provider.getRecs());
            setMeta(provider.getMeta());
        }
        catch (error) {
            // Aborts are an expected control-flow signal, not a failure to log.
            if ((error === null || error === void 0 ? void 0 : error.name) === 'AbortError')
                return;
            console.error('Failed to load recommendations:', error);
        }
        finally {
            // Only the call that "owns" the in-flight slot clears `loading`.
            // A superseded (aborted) call leaves the spinner up because the newer
            // request is still mid-flight — it'll clear `loading` when it lands.
            if (!controller.signal.aborted)
                setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [provider, recsConfigKey]);
    useEffect(() => {
        if (recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.awaitRefresh)
            return;
        loadRecs();
    }, [loadRecs, recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.awaitRefresh]);
    return {
        loading,
        products,
        meta,
        loadRecs,
    };
}
